import React, { useEffect, useMemo, useState } from 'react';
import { Card, Placeholder, ProgressBar } from 'react-bootstrap';
import { getCropPlan } from './satelliteApi';
import { cropProgress } from './cropProgress';
import { t } from './satelliteText';

/** A YYYY-MM-DD as "22 Mar 2026": that calendar day wherever the reader is (farmer-app healthText.formatDay). */
const formatDay = (raw) => {
  if (!raw) return '';
  const s = String(raw);
  const d = /^\d{4}-\d{2}-\d{2}$/.test(s) ? new Date(`${s}T00:00:00`) : new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const capitalise = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/** The crop as we name it, else as the plan names it (farmer-app healthText.cropLabel). */
const cropLabel = (crop) => {
  if (!crop) return '';
  const key = `cropName_${String(crop).toLowerCase().trim()}`;
  const translated = t(key);
  return translated === key ? capitalise(String(crop)) : translated;
};

/** "Day 23 after sowing", "Sowing in 12 days", "Sowing day" (farmer-app GuideHero.dayLabel). */
const dayLabel = (day) => {
  if (day > 0) return t('guideDayAfter', { n: day });
  if (day < 0) return t('guideSowingIn', { n: -day });
  return t('guideSowingToday');
};

/**
 * Crop progress: the farm's crop and where it is today, as the top of the
 * farmer app's Crop guide shows it (GuideHero). Read-only: no plan editing,
 * tasks or tips. The plan is asked for only when this page opens.
 */
export default function CropProgressSection({ farm }) {
  const [state, setState] = useState({ status: 'loading', plan: null });

  useEffect(() => {
    let live = true;
    setState({ status: 'loading', plan: null });
    getCropPlan(farm.id)
      .then((plan) => live && setState({ status: 'ready', plan }))
      .catch(() => live && setState({ status: 'error', plan: null }));
    return () => {
      live = false;
    };
  }, [farm.id]);

  const guide = useMemo(() => (state.status === 'ready' ? cropProgress(state.plan) : null), [state]);

  let body;
  if (state.status === 'loading') {
    body = (
      <Placeholder as="div" animation="glow">
        <Placeholder xs={4} className="mb-2" />
        <Placeholder xs={6} />
      </Placeholder>
    );
  } else if (state.status === 'error') {
    body = <span className="text-muted">Could not load the crop plan.</span>;
  } else if (!guide || guide.status !== 'ready') {
    body = <span className="text-muted">No crop plan has been set up for this farm yet.</span>;
  } else {
    const general = !guide.variety || guide.generic;
    body = (
      <>
        <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
          <strong>
            {cropLabel(guide.cropName)} · {guide.variety || t('guideVarietyUnknown')}
          </strong>
          {general && <span className="badge text-bg-warning">{t('guideGeneralPlan')}</span>}
        </div>
        <div className="h4 fw-bold mb-1">{dayLabel(guide.day)}</div>
        <div className="mb-1">
          <strong>{t(guide.stage.nameKey)}</strong>{' '}
          <span className="text-muted">
            {t('guideEstimatedFrom', { what: t(guide.sown ? 'guideYourSowingDate' : 'guideYourExpectedDate') })}
          </span>
        </div>
        <small className="d-block text-muted mb-2">
          {t(guide.sown ? 'guideSown' : 'guideExpectedSowing', { date: formatDay(guide.sowingDate) })}
        </small>
        <ProgressBar
          now={guide.progressPct}
          variant="success"
          style={{ height: 8 }}
          aria-label={t(guide.stage.nameKey)}
        />
        <div className="d-flex small text-muted mt-1" style={{ justifyContent: 'space-between' }}>
          <span>{t('guideBeforeSowing')}</span>
          <span>{t('guideHarvestAt', { n: guide.total })}</span>
        </div>
      </>
    );
  }

  return (
    <Card body className="mb-4" style={{ maxWidth: 640 }}>
      <h2 className="h6 text-uppercase text-muted fw-bold mb-2">Crop progress</h2>
      {body}
    </Card>
  );
}
